import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  InputAdornment,
  Tooltip,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Plus,
  Pencil,
  Trash2,
  Coins,
  Download,
  Search
} from 'lucide-react';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import { getCustomDomain, getDisplayDomain } from '../utils/domainUtils';

const StyledPaper = styled(Paper)(({ theme }) => ({
  background: '#141926',
  padding: theme.spacing(3),
  borderRadius: '16px',
  border: '1px solid rgba(255,255,255,0.06)',
  marginTop: theme.spacing(3)
}));

const StyledTable = styled(TableContainer)(({ theme }) => ({
  background: '#141926',
  borderRadius: '16px',
  border: '1px solid rgba(255,255,255,0.06)',
  overflowX: 'auto',
  '& .MuiTableCell-root': {
    borderBottom: '1px solid rgba(124, 58, 237, 0.1)',
    color: '#fff',
    whiteSpace: 'nowrap',
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    color: '#7C3AED',
    fontWeight: 600,
  }
}));

const AdminPanel = () => {
  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    nome: '',
    tokens: 5,
    allowedTemplates: ['padrao']
  });
  const [tab, setTab] = useState(0);
  const [searchEmail, setSearchEmail] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);

  useEffect(() => {
    fetchUsers();
    fetchSites();
  }, []);

  useEffect(() => {
    if (searchEmail.trim() === '') {
      setFilteredUsers(users);
    } else {
      setFilteredUsers(users.filter(u => u.email.toLowerCase().includes(searchEmail.toLowerCase())));
    }
  }, [searchEmail, users]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.success) {
        setUsers(response.data.users);
      } else {
        setError(response.data.message);
      }
    } catch (err) {
      setError('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const fetchSites = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/sites`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.success) {
        setSites(response.data.sites);
      }
    } catch (err) {
      // ignora
    }
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({ email: user.email, password: '', nome: user.nome || '', tokens: user.tokens || 5, allowedTemplates: user.allowedTemplates || ['padrao'] });
    } else {
      setEditingUser(null);
      setFormData({ email: '', password: '', nome: '', tokens: 5, allowedTemplates: ['padrao'] });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUser(null);
    setFormData({ email: '', password: '', nome: '', tokens: 5, allowedTemplates: ['padrao'] });
  };

  const handleTemplateToggle = (templateKey) => {
    setFormData(prev => {
      const current = prev.allowedTemplates || ['padrao'];
      if (current.includes(templateKey)) {
        if (current.length <= 1) return prev;
        return { ...prev, allowedTemplates: current.filter(t => t !== templateKey) };
      }
      return { ...prev, allowedTemplates: [...current, templateKey] };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      const url = editingUser
        ? `${process.env.REACT_APP_API_URL}/api/admin/users/${editingUser._id}`
        : `${process.env.REACT_APP_API_URL}/api/admin/users`;
      const method = editingUser ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify(formData)
      });
      const data = await response.json();
      if (data.success) {
        setSuccess(editingUser ? 'Usuário atualizado com sucesso!' : 'Usuário criado com sucesso!');
        handleCloseDialog();
        fetchUsers();
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Erro ao salvar usuário');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Tem certeza que deseja excluir este usuário?')) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (data.success) {
        setSuccess('Usuário excluído com sucesso!');
        fetchUsers();
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Erro ao excluir usuário');
    }
  };

  const handleAddTokens = async (userId) => {
    const quantidade = prompt('Quantos tokens deseja adicionar?');
    if (!quantidade || isNaN(quantidade) || parseInt(quantidade) <= 0) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/add-tokens`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ userId, quantidade: parseInt(quantidade) })
      });
      const data = await response.json();
      if (data.success) {
        setSuccess('Tokens adicionados com sucesso!');
        fetchUsers();
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Erro ao adicionar tokens');
    }
  };

  const downloadCSV = (filename, header, rows) => {
    const bom = '\uFEFF';
    const csv = bom + header + '\n' + rows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportEmails = () => {
    const header = 'Nome,Email';
    const rows = users.map(u => `"${(u.nome || '').replace(/"/g, '""')}","${u.email}"`);
    downloadCSV('emails_clientes.csv', header, rows);
  };

  const handleExportContatos = () => {
    const header = 'Nome,Email,Telefone,CPF';
    const rows = users.map(u =>
      `"${(u.nome || '').replace(/"/g, '""')}","${u.email}","${u.telefone || ''}","${u.cpf || ''}"`
    );
    downloadCSV('contatos_clientes.csv', header, rows);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Usuários" />
        <Tab label="Histórico de Sites" />
      </Tabs>
      {tab === 0 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h4" sx={{ color: '#fff' }}>
              Painel de Administração
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Tooltip title="Baixar CSV com nome e email de todos os clientes">
                <Button
                  variant="outlined" size="small" startIcon={<Download size={20} strokeWidth={1.75} />}
                  onClick={handleExportEmails}
                  sx={{ borderColor: 'rgba(124,58,237,0.4)', color: '#A78BFA', textTransform: 'none', '&:hover': { borderColor: '#7C3AED', background: 'rgba(124,58,237,0.08)' } }}
                >
                  Exportar Emails
                </Button>
              </Tooltip>
              <Tooltip title="Baixar CSV com nome, email, telefone e CPF de todos os clientes">
                <Button
                  variant="outlined" size="small" startIcon={<Download size={20} strokeWidth={1.75} />}
                  onClick={handleExportContatos}
                  sx={{ borderColor: 'rgba(124,58,237,0.4)', color: '#A78BFA', textTransform: 'none', '&:hover': { borderColor: '#7C3AED', background: 'rgba(124,58,237,0.08)' } }}
                >
                  Exportar Contatos
                </Button>
              </Tooltip>
              <Button
                variant="contained" startIcon={<Plus size={20} strokeWidth={1.75} />} onClick={() => handleOpenDialog()}
                sx={{ background: '#7C3AED', color: '#fff', textTransform: 'none', '&:hover': { background: '#8B5CF6' } }}
              >
                Novo Usuário
              </Button>
            </Box>
          </Box>
          <TextField
            fullWidth placeholder="Buscar usuário por email" value={searchEmail}
            onChange={e => setSearchEmail(e.target.value)} sx={{ mb: 2 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search size={20} strokeWidth={1.75} /></InputAdornment> }}
          />
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
          <StyledPaper>
            <StyledTable>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Nome</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Telefone</TableCell>
                    <TableCell>Tokens</TableCell>
                    <TableCell>Templates</TableCell>
                    <TableCell>Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredUsers.map((user) => (
                    <TableRow key={user._id}>
                      <TableCell>{user.nome}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.telefone || '-'}</TableCell>
                      <TableCell>{user.tokens}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', color: '#9CA3AF' }}>
                        {(user.allowedTemplates || ['padrao']).map(t => t === 'padrao' ? 'Padrão' : 'Organic').join(', ')}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Editar usuário">
                          <IconButton onClick={() => handleOpenDialog(user)} color="primary" size="small">
                            <Pencil size={20} strokeWidth={1.75} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir usuário">
                          <IconButton onClick={() => handleDelete(user._id)} color="error" size="small">
                            <Trash2 size={20} strokeWidth={1.75} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Adicionar tokens">
                          <IconButton onClick={() => handleAddTokens(user._id)} color="success" size="small">
                            <Coins size={20} strokeWidth={1.75} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </StyledTable>
          </StyledPaper>
        </>
      )}
      {tab === 1 && (
        <StyledPaper>
          <Typography variant="h5" sx={{ color: '#fff', mb: 2 }}>
            Histórico de Sites Criados
          </Typography>
          <StyledTable>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Usuário</TableCell>
                  <TableCell>Domínio</TableCell>
                  <TableCell>Data de Criação</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>URL</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sites.map((site) => (
                  <TableRow key={site._id}>
                    <TableCell>{site.user?.nome || site.user || '-'}</TableCell>
                    <TableCell>{site.name}</TableCell>
                    <TableCell>{new Date(site.createdAt || site.dataCriacao).toLocaleString('pt-BR')}</TableCell>
                    <TableCell>{site.status}</TableCell>
                    <TableCell>
                      {site.url ? (
                        <a href={getCustomDomain(site)} target="_blank" rel="noopener noreferrer" style={{ color: '#7C3AED' }}>
                          {getDisplayDomain(site)}
                        </a>
                      ) : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </StyledTable>
        </StyledPaper>
      )}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth
        PaperProps={{ sx: { background: '#1F2937', border: '1px solid rgba(124,58,237,0.2)', borderRadius: 3 } }}
      >
        <DialogTitle sx={{ color: '#fff' }}>
          {editingUser ? 'Editar Usuário' : 'Novo Usuário'}
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <TextField
              fullWidth label="Nome" value={formData.nome}
              onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
              margin="normal" required
            />
            <TextField
              fullWidth label="Email" type="email" value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              margin="normal" required
            />
            <TextField
              fullWidth label={editingUser ? "Nova Senha (opcional)" : "Senha"} type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              margin="normal" required={!editingUser}
              helperText={editingUser ? "Deixe em branco para manter a senha atual" : "Mínimo 6 caracteres"}
            />
            <TextField
              fullWidth label="Tokens" type="number" value={formData.tokens}
              onChange={(e) => setFormData({ ...formData, tokens: parseInt(e.target.value) || 0 })}
              margin="normal" required
            />
            {editingUser && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ color: '#9CA3AF', mb: 1 }}>Templates permitidos:</Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={(formData.allowedTemplates || []).includes('padrao')}
                      onChange={() => handleTemplateToggle('padrao')}
                      sx={{ color: '#7C3AED', '&.Mui-checked': { color: '#7C3AED' } }}
                    />
                  }
                  label={<Typography sx={{ color: '#fff', fontSize: '0.9rem' }}>Template Padrão</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={(formData.allowedTemplates || []).includes('organic')}
                      onChange={() => handleTemplateToggle('organic')}
                      sx={{ color: '#7C3AED', '&.Mui-checked': { color: '#7C3AED' } }}
                    />
                  }
                  label={<Typography sx={{ color: '#fff', fontSize: '0.9rem' }}>Template Organic</Typography>}
                />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDialog} sx={{ color: '#9CA3AF' }}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained"
            sx={{ background: '#7C3AED', '&:hover': { background: '#8B5CF6' } }}
          >
            {editingUser ? 'Atualizar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminPanel;
